import { useCallback, useEffect, useRef, useState, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Download, Pencil, Trash2, Upload, History, FileText, Stamp, Truck, AlertTriangle } from "lucide-react";
import { MODULES } from "@/config/modules";
import { api, errMsg, downloadExcel } from "@/lib/api";
import { DynamicSelect } from "@/components/DynamicSelect";
import { FileField } from "@/components/FileField";
import { ExtractButton, BulkUploadButton } from "@/components/SmartUpload";
import { ResultsGrid } from "@/components/ResultsGrid";
import { DeltaWarning } from "@/components/DeltaWarning";
import { EquipmentCoverage } from "@/components/EquipmentCoverage";
import { FilterBar, emptyFilters, filterParams } from "@/components/FilterBar";
import { loadFilters, saveFilters } from "@/lib/filterStore";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { ChainOfCustody, ChainToggle } from "@/components/ChainOfCustody";
import { UlidField } from "@/components/UlidField";
import { SignaturePad } from "@/components/SignaturePad";
import { BarcodeScanField } from "@/components/BarcodeScanField";
import { TraceabilityBulk } from "@/components/TraceabilityBulk";
import { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ModulePage() {
  const { module } = useParams();
  const navigate = useNavigate();
  const { user, allowedModules, options, loadOptions } = useAuth();
  const cfg = MODULES[module];
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdf, setPdf] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [audit, setAudit] = useState(null);
  const [chainOpen, setChainOpen] = useState(null);   // { module, recordId }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [masterPdf, setMasterPdf] = useState(false);

  const masterPdfPath = () => {
    const labels = cfg.columns.map((c) =>
      cfg.fields.find((f) => f.name === c)?.label || c.replace(/_/g, " "));
    const params = new URLSearchParams({
      ...filterParams(applied), columns: cfg.columns.join(","), labels: labels.join(","),
    });
    return `/master-report/${module}?${params.toString()}`;
  };
  const scanFilledRef = useRef([]);
  const loadSeqRef = useRef(0);
  const tableWrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(null);
  const [gaps, setGaps] = useState({});

  useEffect(() => {
    if (module !== "samples" && module !== "traceability") { setGaps({}); return; }
    let alive = true;
    api.get("/custody/gaps", { params: { module } })
      .then(({ data }) => { if (alive) setGaps(data.gaps || {}); })
      .catch((e) => console.warn("custody gaps skipped:", e));
    return () => { alive = false; };
  }, [module, records]);

  useEffect(() => {
    const measure = () => setWrapW(tableWrapRef.current?.clientWidth || null);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading]);

  const toggleChain = (recordId) => {
    const isOpen = chainOpen?.recordId === recordId && chainOpen?.module === module;
    setChainOpen(isOpen ? null : { module, recordId });
    // keep the expanded strip in view on a horizontally scrolled table
    if (!isOpen && tableWrapRef.current) tableWrapRef.current.scrollLeft = 0;
  };

  const isChainOpen = (recordId) =>
    chainOpen?.recordId === recordId && chainOpen?.module === module;

  const [filters, setFilters] = useState(() => loadFilters(module, emptyFilters));
  const [applied, setApplied] = useState(() => loadFilters(module, emptyFilters));
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [highlight, setHighlight] = useState(null);

  // a "jump to record" link widens the view for that visit only; saved filters stay untouched
  useEffect(() => {
    const next = focusId ? { ...emptyFilters } : loadFilters(module, emptyFilters);
    setFilters(next);
    setApplied(next);
    setChainOpen(null);
  }, [module, focusId]);

  useEffect(() => {
    if (!focusId || loading) return;
    const row = document.querySelector(`[data-testid="row-${focusId}"]`);
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setChainOpen({ module, recordId: focusId });
    setHighlight(focusId);
    const t = setTimeout(() => setHighlight(null), 6000);
    return () => clearTimeout(t);
  }, [focusId, loading, records, module]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const seq = ++loadSeqRef.current;
      const { data } = await api.get(`/records/${module}`, { params: filterParams(applied) });
      // a slower earlier request must never overwrite the newest list
      if (seq !== loadSeqRef.current) return;
      setRecords(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [module, applied]);

  useEffect(() => { load(); }, [load]);

  const filtered = records;

  if (!cfg) return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-6" data-testid="module-removed">
      <p className="font-heading font-bold text-sm text-[#111827]">This module is no longer available.</p>
      <p className="text-xs text-[#6B7280] mt-1">
        Inventory and indents are now handled by the haematology sub-store — use Item Master, Stock
        Transactions and the Indent Planner in the sidebar.
      </p>
      <Button className="mt-3 bg-[#002FA7] hover:bg-[#00237C] text-white" data-testid="module-removed-home"
        onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
    </div>
  );
  if (user?.role !== "admin" && allowedModules.length > 0 && !allowedModules.includes(module)) {
    return (
      <div className="rounded-md border border-[#E5E7EB] bg-white p-8 text-center" data-testid="module-access-denied">
        <p className="font-heading font-bold text-[#111827]">Not included in your access plan</p>
        <p className="text-sm text-[#6B7280] mt-1">
          Ask an administrator to grant you access to {cfg.label}.
        </p>
      </div>
    );
  }

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...(r.data || {}) }); setOpen(true); };

  const sigValue = (field) => {
    const sig = form[`${field.role}_signature`];
    if (!sig?.file_id) return null;
    return { ...sig, preview_url: fileUrl(sig.file_id) };
  };

  const captureSignature = async (field, payload) => {
    if (!editing) {
      toast.error("Save the entry first, then capture the signature");
      return;
    }
    try {
      const { data } = await api.post(`/traceability/${editing.record_id}/signature`, {
        image: payload.image, signer_name: payload.signer_name, role: field.role,
      });
      setForm((prev) => ({ ...prev, [`${field.role}_signature`]: data.signature,
                           [`${field.role}_ack`]: "Yes" }));
      toast.success("Signature saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    const missing = cfg.fields.filter((f) => f.required && f.type !== "computed" && !String(form[f.name] || "").trim());
    if (missing.length) return toast.error(`Required: ${missing.map((m) => m.label).join(", ")}`);
    const ulidField = cfg.fields.find((f) => f.type === "ulid");
    const ulid = ulidField ? String(form[ulidField.name] || "").trim() : "";
    if (ulid) {
      try {
        const { data } = await api.get("/patients/check",
          { params: { ulid, name: form.patient_name || "" } });
        if (data.status === "mismatch"
            && !window.confirm(`${data.message}\n\nSave anyway?`)) return;
      } catch { /* the registry check is advisory only */ }
    }
    setSaving(true);
    try {
      const payload = { data: form };
      if (editing) await api.put(`/records/${module}/${editing.record_id}`, payload);
      else await api.post(`/records/${module}`, payload);
      toast.success(editing ? "Record updated" : "Record added");
      const day = String(payload.data?.received_on || payload.data?.collection_date
        || payload.data?.report_date || "").slice(0, 10);
      if (day && applied.date_from && (day < applied.date_from || (applied.date_to && day > applied.date_to))) {
        const widened = { ...applied, date_from: day < applied.date_from ? day : applied.date_from,
          date_to: applied.date_to && day > applied.date_to ? day : applied.date_to };
        saveFilters(module, widened);
        setFilters(widened);
        setApplied(widened);
        toast.info(`Date filter widened to ${day} so the saved record stays visible`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/records/${module}/${r.record_id}`);
      toast.success("Record deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportExcel = async () => {
    try {
      await downloadExcel(module, cfg.label, filterParams(applied));
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const importExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fieldMap = {};
      cfg.fields.forEach((fl) => { fieldMap[fl.label] = fl.name; fieldMap[fl.name] = fl.name; });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("field_map", JSON.stringify(fieldMap));
      const { data } = await api.post(`/import/${module}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} records${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting(false);
    }
  };

  const absorbExtracted = async (vals) => {
    // Extracted dropdown values may not exist yet — add them to the master list so the select shows them.
    const additions = [];
    for (const field of cfg.fields) {
      if (field.type !== "select" || !field.category) continue;
      const raw = String(vals[field.name] || "").trim();
      if (!raw) continue;
      const existing = (options[field.category] || []);
      const match = existing.find((o) => o.toLowerCase() === raw.toLowerCase());
      if (match) {
        vals[field.name] = match;
      } else {
        additions.push({ category: field.category, value: raw });
      }
    }
    if (additions.length) {
      await Promise.all(additions.map((a) => api.post("/options", a)
        .catch((e) => console.warn("could not add dropdown option:", a, e))));
      await loadOptions();
      toast.success(`Added ${additions.length} new dropdown value(s) found in the document`);
    }
    setForm((p) => ({ ...p, ...vals }));
  };

  const downloadPatientReport = (r) => setPdf({
    path: `/samples/${r.record_id}/report.pdf`,
    filename: `Report_${r.data?.sample_id || r.record_id.slice(0, 8)}.pdf`,
    title: `Signed patient report — ${r.data?.patient_name || r.data?.sample_id || ""}`,
  });

  const downloadPdf = (r) => setPdf({
    path: `/report/${module}/${r.record_id}`,
    filename: `LabTrack_${module}_${r.record_id.slice(0, 8)}.pdf`,
    title: `${cfg.label} — ${r.data?.sample_id || r.record_id.slice(0, 8)}`,
  });

  const showHistory = async (r) => {
    try {
      const { data } = await api.get(`/audit/${module}/${r.record_id}`);
      setAudit({ record: r, entries: data });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const labelOf = (name) => cfg.fields.find((f) => f.name === name)?.label || name;

  // Choosing a machine pulls its known details forward from the equipment log.
  const autofillEquipment = async (analyzer) => {
    if (!analyzer || !["samples", "qc", "sops", "proficiency"].includes(module)) return;
    try {
      const { data } = await api.get(`/equipment/master/${encodeURIComponent(analyzer)}`);
      if (data?.known === false) return;
      const names = new Set(cfg.fields.map((fl) => fl.name));
      const carry = ["serial_number", "department", "vendor", "equipment_status", "performed_by", "technician"];
      setForm((p) => {
        const next = { ...p };
        carry.forEach((k) => {
          if (names.has(k) && !String(next[k] || "").trim() && data[k]) next[k] = data[k];
        });
        return next;
      });
      toast.success(`Details auto-filled from the equipment log for ${analyzer}`);
    } catch (e) { console.warn("equipment autofill skipped:", e); }
  };

  const cellValue = (r, c) => {
    const v = r.data?.[c];
    if (!v) return "—";
    if (typeof v === "object") return Object.keys(v).length ? `${Object.keys(v).length} values` : "—";
    return String(v).includes("|") ? String(v).split("|")[0] : String(v);
  };

  return (
    <div data-testid={`module-page-${module}`}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">{cfg.label}</h1>
          <p className="text-sm text-[#6B7280] mt-1">{cfg.desc}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label data-testid="import-excel-label"
            className="inline-flex items-center h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm cursor-pointer hover:border-[#002FA7] transition-colors">
            <Upload className="h-4 w-4 mr-2 text-[#002FA7]" /> {importing ? "Importing…" : "Import"}
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="import-excel-input"
              onChange={(e) => { importExcel(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn">
            <Download className="h-4 w-4 mr-2 text-[#2E7D32]" /> Excel
          </Button>
          <Button variant="outline" onClick={() => setMasterPdf(true)} data-testid="master-pdf-btn">
            <FileText className="h-4 w-4 mr-2 text-[#C92A2A]" /> Master PDF
          </Button>
          <BulkUploadButton module={module} fields={cfg.fields} onDone={load} />
          {module === "traceability" && (
            <Button variant="outline" onClick={() => setBulkOpen((v) => !v)} data-testid="bulk-trace-open">
              <Truck className="h-4 w-4 mr-2 text-[#002FA7]" /> Log a delivery trip
            </Button>
          )}
          <Button onClick={openNew} data-testid="add-record-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Plus className="h-4 w-4 mr-2" /> New Entry
          </Button>
        </div>
      </div>

      {module === "traceability" && bulkOpen && (
        <TraceabilityBulk onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); load(); }} />
      )}

      <div className="mt-6 rounded-md border border-[#E5E7EB] bg-white">
        <FilterBar value={filters} onChange={setFilters}
          onApply={(next) => {
            const use = { ...(next || filters) };
            saveFilters(module, use);
            setApplied(use);
          }}
          selects={{
            ...(cfg.fields.some((f) => f.name === "status") ? { status: options.status || [] } : {}),
            ...(cfg.fields.some((f) => f.name === "test_name") ? { test_name: options.test_name || [] } : {}),
            ...(cfg.fields.some((f) => f.name === "analyzer") ? { analyzer: options.analyzer || [] } : {}),
            ...(cfg.fields.some((f) => f.name === "priority") ? { priority: options.priority || [] } : {}),
          }} />
        <div className="px-3 py-2 border-b border-[#E5E7EB] flex justify-end">
          <span className="text-xs text-[#6B7280]" data-testid="record-count">{filtered.length} records</span>        </div>
        <div className="overflow-x-auto" ref={tableWrapRef}>
          <table className="w-full text-sm" data-testid="records-table">
            <thead className="bg-[#F3F4F6] sticky top-0">
              <tr>
                {cfg.columns.map((c) => (
                  <th key={c} className="text-left font-medium text-xs uppercase tracking-[0.08em] text-[#6B7280] px-3 py-2 whitespace-nowrap">
                    {labelOf(c)}
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-xs uppercase tracking-[0.08em] text-[#6B7280]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cfg.columns.length + 1} className="px-3 py-8 text-center text-[#6B7280]">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={cfg.columns.length + 1} className="px-3 py-10 text-center text-[#6B7280]" data-testid="empty-state">
                  No records yet. Click “New Entry” to add the first one.
                </td></tr>
              )}
              {!loading && filtered.map((r) => (
                <Fragment key={r.record_id}>
                <tr className={`border-t border-[#E5E7EB] hover:bg-gray-50 ${
                  highlight === r.record_id ? "bg-[#EEF2FF] ring-2 ring-inset ring-[#002FA7]" : ""}`}
                  data-testid={`row-${r.record_id}`}>
                  {cfg.columns.map((c, ci) => (
                    <td key={c} className="px-3 py-2 text-[#111827] whitespace-nowrap">
                      <div className="flex items-center gap-2 max-w-[240px]">
                        <span className="truncate">{cellValue(r, c)}</span>
                        {ci === 0 && gaps[r.record_id] && (
                          <span title={gaps[r.record_id]} data-testid={`custody-gap-${r.record_id}`}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#FFF4E5] text-[#B45309]
                              border border-[#F5C77E] px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]">
                            <AlertTriangle className="h-3 w-3" /> Custody gap
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {(module === "samples" || module === "traceability") && (
                      <ChainToggle recordId={r.record_id} open={isChainOpen(r.record_id)}
                        onToggle={() => toggleChain(r.record_id)} />
                    )}                    {module === "samples" && (
                      <button onClick={() => downloadPatientReport(r)} data-testid={`patient-report-${r.record_id}`}
                        title="Signed patient report (letterhead + signature)"
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#2E7D32]"><Stamp className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => downloadPdf(r)} data-testid={`pdf-${r.record_id}`}
                      title="Printable PDF report" className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]"><FileText className="h-4 w-4" /></button>
                    <button onClick={() => showHistory(r)} data-testid={`history-${r.record_id}`}
                      title="Audit trail" className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><History className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(r)} data-testid={`edit-${r.record_id}`}
                      className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(r)} data-testid={`delete-${r.record_id}`}
                      className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
                {isChainOpen(r.record_id) && (
                  <tr className="border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid={`chain-row-${r.record_id}`}>
                    <td colSpan={cfg.columns.length + 1} className="p-0">
                      <div className="sticky left-0 px-3 py-3"
                        style={{ width: wrapW || "100%" }}>
                        <ChainOfCustody key={`${module}-${r.record_id}`} module={module}
                          recordId={r.record_id}
                          onOpenPartner={(chain) => navigate(`/m/${chain.module}?focus=${chain.record_id}`)} />
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {module === "equipment" && <EquipmentCoverage onCreated={load} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="record-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? "Edit" : "New"} — {cfg.label}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Fill the fields below. Use the + button beside any dropdown to add a new option instantly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 [&>*]:min-w-0">
            {(module === "samples" || module === "traceability") && !editing && (
              <div className="sm:col-span-2">
                <BarcodeScanField testid="record-barcode" module={module} onResolved={(res) => {
                  const p = res.prefill || {};
                  const prevScanned = scanFilledRef.current;
                  const byName = new Map(cfg.fields.map((f) => [f.name, f]));
                  const scanned = {};
                  Object.entries(p).forEach(([k, v]) => {
                    const key = module === "traceability" && k === "patient_id" ? "uhid"
                      : module === "traceability" && k === "sample_type" ? "nature_of_sample" : k;
                    const field = byName.get(key);
                    if (!field || !v) return;
                    let value = v;
                    if (field.type === "select" && field.category) {
                      const master = options[field.category] || [];
                      value = master.find((o) => o.toLowerCase() === String(v).toLowerCase()) || v;
                    }
                    const typed = String(form[key] || "").trim();
                    // a fresh scan replaces what the previous scan filled, never what was typed
                    if (!typed || prevScanned.includes(key)) scanned[key] = value;
                  });
                  scanFilledRef.current = Object.keys(scanned);
                  setForm((prev) => ({ ...prev, ...scanned }));
                }} />
              </div>
            )}
            <ExtractButton module={module} fields={cfg.fields} onExtracted={absorbExtracted} />
            {module === "samples" && (
              <>
                <DeltaWarning patient={form.patient_id || form.patient_name} testName={form.test_name}
                  results={form.results || {}} />
                <ResultsGrid testName={form.test_name} sampleId={form.sample_id} analyzer={form.analyzer}
                  results={form.results || {}} onChange={(results) => setForm((p) => ({ ...p, results }))} />
              </>
            )}
            {cfg.fields.map((field) => {
              const val = form[field.name] || "";
              const onChange = (v) => {
                setForm((p) => ({ ...p, [field.name]: v }));
                if (field.name === "analyzer") autofillEquipment(v);
              };
              return (
                <div key={field.name} className={field.type === "textarea" || field.type === "file"
                  || field.type === "signature" ? "sm:col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                    {field.label}{field.required && <span className="text-[#C92A2A]"> *</span>}
                  </Label>
                  {field.type === "signature" ? (
                    <div className="mt-1.5">
                      <SignaturePad label={field.label} testid={`sig-${field.role}`}
                        signerLabel={field.role === "attender" ? "Attender name" : "Technician name"}
                        value={sigValue(field)}
                        onSave={(p) => captureSignature(field, p)}
                        onClear={() => setForm((prev) => ({ ...prev, [`${field.role}_signature`]: null }))} />
                      {!editing && (
                        <p className="text-[10px] text-[#6B7280] mt-1">
                          Save the entry first, then sign — the signature attaches to this entry.
                        </p>
                      )}
                    </div>
                  ) : field.type === "ulid" ? (
                    <UlidField field={field} value={val} onChange={onChange}
                      patientName={form.patient_name || ""} />
                  ) : field.type === "select" ? (
                    <DynamicSelect field={field} value={val} onChange={onChange} />
                  ) : field.type === "computed" ? (
                    <div className="mt-1.5">
                      <Input value={val} readOnly disabled data-testid={`input-${field.name}`}
                        placeholder="Auto-calculated" className="bg-[#F3F4F6]" />
                      <p className="text-[10px] text-[#6B7280] mt-1">{field.hint}</p>
                    </div>
                  ) : field.type === "file" ? (
                    <FileField field={field} value={val} onChange={onChange} />
                  ) : field.type === "textarea" ? (
                    <Textarea value={val} onChange={(e) => onChange(e.target.value)} rows={2}
                      data-testid={`input-${field.name}`} className="mt-1.5" />
                  ) : (
                    <Input type={field.type} value={val} onChange={(e) => onChange(e.target.value)}
                      data-testid={`input-${field.name}`} className="mt-1.5" />
                  )}
                </div>
              );
            })}
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-record-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-record-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
                {saving ? "Saving…" : editing ? "Update Record" : "Save Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!audit} onOpenChange={(v) => !v && setAudit(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Audit Trail</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Every change to this record, with the previous values kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(audit?.entries || []).length === 0 && (
              <p className="text-sm text-[#6B7280]" data-testid="audit-empty">No history available for this record.</p>
            )}
            {(audit?.entries || []).map((a) => (
              <div key={a.audit_id} className="rounded-md border border-[#E5E7EB] p-3" data-testid={`audit-entry-${a.audit_id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize text-[#111827]">{a.action}</span>
                  <span className="text-xs text-[#6B7280]">{String(a.at).slice(0, 19).replace("T", " ")}</span>
                </div>
                <p className="text-xs text-[#6B7280] mt-0.5">by {a.user} ({a.role})</p>
                {(a.changes || []).length > 0 && (
                  <table className="w-full text-xs mt-2">
                    <thead>
                      <tr className="text-[#6B7280]">
                        <th className="text-left py-1">Field</th><th className="text-left py-1">Old</th><th className="text-left py-1">New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.changes.map((c) => (
                        <tr key={c.field} className="border-t border-[#E5E7EB]">
                          <td className="py-1 pr-2">{labelOf(c.field)}</td>
                          <td className="py-1 pr-2 text-[#C92A2A]">{String(c.from) || "—"}</td>
                          <td className="py-1 text-[#2E7D32]">{String(c.to) || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={!!pdf} onClose={() => setPdf(null)} path={pdf?.path}
        filename={pdf?.filename} title={pdf?.title} />

      <PdfPreviewDialog open={masterPdf} onClose={() => setMasterPdf(false)}
        path={masterPdf ? masterPdfPath() : null}
        filename={`labtrack_${module}_master.pdf`}
        title={`${cfg.label} — Master Log (current filters)`} />
    </div>
  );
}
